import axios from 'axios';

class SalesforceApiService {
  constructor() {
    this.baseUrl = process.env.NODE_ENV === 'production' 
      ? 'https://cerebralstaffing.my.salesforce-sites.com/services/apexrest'
      : '/services/apexrest';
    this.api = axios.create({
      baseURL: this.baseUrl,
      headers: {
        'Content-Type': 'application/json'
      }
    });

    this.api.interceptors.response.use(
      response => response,
      error => Promise.reject(error)
    );
  }

  async getJobs() {
    try {
      const params = { Job_Type: 'Clinical' };
      const response = await this.api.get('/cerebralstaffing', { params });
      if (!response.data) {
        throw new Error('No data received from API');
      }
      return response.data || [];
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw new Error('Failed to fetch jobs. Check console for details.');
    }
  }

  async getJobDetails(jobId) {
    try {
      // First try to get regular job from cerebralstaffing endpoint
      try {
        const params = { Record_Id: jobId };
        const response = await this.api.get('/cerebralstaffing', { params });
        const jobs = response.data || [];
        if (jobs.length > 0) {
          return jobs[0];
        }
      } catch (regularJobError) {
        // If regular job not found, continue to check adhoc jobs
        console.log('Regular job not found, checking adhoc jobs');
      }

      // If regular job not found, try adhoc job from jobportal endpoint
      const adhocParams = { adhocJobId: jobId };
      const adhocResponse = await this.api.get('/jobportal/', { params: adhocParams });
      
      if (adhocResponse.data && adhocResponse.data.data) {
        return adhocResponse.data.data;
      }
      
      throw new Error('Job not found');
    } catch (error) {
      console.error('Error fetching job details:', error);
      throw new Error('Failed to fetch job details. Job may not exist or cannot be accessed.');
    }
  }

  async getPortalSources() {
    try {
      const response = await this.api.get('/cerebralstaffportal');
      return response.data || [];
    } catch (error) {
      console.error('Error fetching portal sources:', error);
      throw new Error('Failed to fetch portal sources');
    }
  }

  async checkReferenceStatus(referenceId) {
    try {
      const params = { 
        id: referenceId,
        _t: Date.now() // Cache busting parameter
      };
      const response = await this.api.get('/referencestatus', { 
        params,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return response.data || {};
    } catch (error) {
      console.error('Error checking reference status:', error);
      throw new Error('Failed to check reference status');
    }
  }

  async checkCandidateReferenceStatus(contactId) {
    try {
      const params = { 
        id: contactId,
        _t: Date.now() // Cache busting parameter
      };
      const response = await this.api.get('/candidatereferencestatus', { 
        params,
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      return response.data || {};
    } catch (error) {
      console.error('Error checking candidate reference status:', error);
      throw new Error('Failed to check candidate reference status');
    }
  }

  async submitJobApplication(applicationData, resumeFile, botDetectionData = null) {
    try {
      const hasResume = !!resumeFile;

      let payload;

      if (hasResume) {
        const resumeBase64 = await this.convertFileToBase64(resumeFile);

        const contactValues = {
          "Candidate_Source__c": "Capstone Website",
          "AVTRRT__Source__c": "Add Resume"
        };

        const contactValuesJson = JSON.stringify(contactValues);

        payload = {
          applicationData: {
            firstName: applicationData.firstName,
            lastName: applicationData.lastName,
            resumeFileName: resumeFile.name,
            contactValues: contactValuesJson
          },
          resume: resumeBase64
        };

        if (applicationData.jobId) {
          payload.applicationData.jobId = applicationData.jobId;
        }
      } else {
        payload = {
          applicationData: {
            firstName: applicationData.firstName,
            lastName: applicationData.lastName,
            email: applicationData.email,
            phone: applicationData.phone,
            hasResume: false
          }
        };

        if (applicationData.jobId) {
          payload.applicationData.jobId = applicationData.jobId;
        }
      }

      // Add bot detection data to payload
      if (botDetectionData) {
        payload.botDetection = {
          honeypotValue: botDetectionData.honeypotValue || '',
          formTimingMs: botDetectionData.formTimingMs || 0,
          v3Token: botDetectionData.v3Token || null,
          v2Token: botDetectionData.v2Token || null,
          action: botDetectionData.action || 'form_submit'
        };
      }

      const response = await this.api.post('/jobportal/v1/apply', payload);
      return {
        success: true,
        message: response.data.message || (hasResume ? 'Resume uploaded successfully' : 'Application submitted successfully')
      };
    } catch (error) {
      console.error('Error submitting application:', error);

      // Check if bot detection failed
      if (error.response?.status === 403) {
        throw new Error(error.response?.data?.error || 'Security verification failed. Please refresh and try again.');
      }

      throw new Error(error.response?.data?.error || 'Failed to submit application. Please try again later.');
    }
  }

  getFileExtension(filename) {
    const ext = filename.split('.').pop().toLowerCase();
    if (ext === 'pdf') return 'pdf';
    if (ext === 'doc') return 'doc';
    if (ext === 'docx') return 'docx';
    return 'pdf';
  }
  
  async convertFileToBase64(file) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

}

const salesforceApiService = new SalesforceApiService();

export default salesforceApiService;